import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  COUPON_COPY_FEEDBACK_DURATION_MS,
  COUPON_FORM_DEFAULT_STEP_ID,
  DISCOUNT_TYPES,
  ZERO,
} from "constants/coupons";
import {
  buildDiscountPreviewItems,
  buildPreviewPackageData,
  buildPreviewPlatformItems,
  buildUsagePreviewItems,
  buildValidityPreviewItems,
  getCouponFormStepSubtitle,
  getCouponValidityDays,
} from "helpers/couponFormPreview";
import {
  AMOUNT_INPUT_PATTERN,
  STEP_BY_FIELD,
  buildNextCurrencySettingsValues,
  buildNextValues,
  getTotalCouponCountMinError,
  isPercentageDiscountOverMax,
} from "helpers/couponFormState";
import {
  applyConfirmedTenantScope,
  applyTenantsScopeChange,
  applyVerticalScopeChange,
  DISCOUNT_TYPE_SCOPE_ERROR_FIELDS,
  TENANT_SCOPE_ERROR_FIELDS,
  VERTICAL_SCOPE_ERROR_FIELDS,
} from "helpers/couponScopeFormState";
import {
  buildCouponFormTenantOptions,
  enrichTenantOptionsWithCurrency,
  getTenantOptionIds,
  mergeCurrencyOptionsWithTenantDetails,
  normalizeTenantIdList,
} from "helpers/couponScopeOptions";
import {
  getCurrencyLabel,
  getSelectedCurrencyIdsFromTenants,
  hasSelectedDiscountType,
  isFlatDiscount,
  isPercentageDiscount,
} from "helpers/couponDiscountType";
import {
  validateCreateCouponForm,
  validateEditCouponForm,
} from "helpers/couponValidation";
import {
  normalizeDecimalInput,
  normalizeIntegerInput,
} from "helpers/numericInput";
import {
  useCouponScopeCurrencyOptions,
  useCouponScopeTenantsByVerticalId,
  useCouponScopeVerticalOptions,
  useBasicPackagesInfo,
} from "store/backend/apiHooks";
import {
  getCouponStatusLabel,
  resolveCouponStatus,
} from "utils/couponUtils";
import { useSnackbarContext } from "widgets/SnackBar/SnackBar";

const useCouponForm = ({
  mode = "create",
  initialValues,
  onSubmit,
  isSubmitting = false,
}) => {
  const navigate = useNavigate();
  const { openSnackbar } = useSnackbarContext();
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [collapsedSections, setCollapsedSections] = useState({});
  const [previewPackageSearch, setPreviewPackageSearch] = useState("");
  const [codeCopied, setCodeCopied] = useState(false);
  const [areTenantsConfirmed, setAreTenantsConfirmed] = useState(
    () =>
      mode !== "create" &&
      normalizeTenantIdList(initialValues?.tenantIds).length > ZERO
  );

  const isView = mode === "view";
  const isCreate = mode === "create";
  const isEdit = mode === "edit";
  const shouldLoadScopeOptions = isCreate || isEdit || isView;
  const submitLabel = isCreate ? "Save Coupon" : "Update Coupon";

  const {
    data: verticalOptions = [],
    isLoading: isVerticalOptionsLoading,
    isError: isVerticalOptionsError,
    error: verticalOptionsError,
    refetch: refetchVerticalOptions,
  } = useCouponScopeVerticalOptions(shouldLoadScopeOptions);

  const {
    data: mdsCurrencyOptions = [],
    isLoading: isCurrencyOptionsLoading,
    isError: isCurrencyOptionsError,
    error: currencyOptionsError,
    refetch: refetchCurrencyOptions,
  } = useCouponScopeCurrencyOptions(shouldLoadScopeOptions);

  const currencyOptions = useMemo(
    () =>
      mergeCurrencyOptionsWithTenantDetails(
        mdsCurrencyOptions,
        values.tenantDetails ?? []
      ),
    [mdsCurrencyOptions, values.tenantDetails]
  );

  const hasSelectedVertical =
    String(values.verticalId ?? "").trim().length > ZERO;
  const isFlat = isFlatDiscount(values.discountType);
  const isPercentage = isPercentageDiscount(values.discountType);
  const hasDiscountType = hasSelectedDiscountType(values.discountType);
  const selectedTenantIds = useMemo(
    () => normalizeTenantIdList(values.tenantIds),
    [values.tenantIds]
  );

  const apiTenantOptions = useMemo(
    () => values.tenantDetails ?? [],
    [values.tenantDetails]
  );

  const hasSelectedTenants = selectedTenantIds.length > ZERO;
  const shouldFetchPackages =
    areTenantsConfirmed &&
    hasSelectedTenants &&
    (values.selectAllPackages === false || isView || isCreate);
  const shouldLoadTenants =
    (isCreate || isEdit) && hasSelectedVertical && hasDiscountType;

  const {
    data: tenantsForVertical = [],
    isLoading: isTenantOptionsLoading,
    isError: isTenantOptionsError,
    error: tenantOptionsError,
    refetch: refetchTenantOptions,
  } = useCouponScopeTenantsByVerticalId(values.verticalId, shouldLoadTenants);

  const enrichedTenantsForVertical = useMemo(
    () => enrichTenantOptionsWithCurrency(tenantsForVertical, currencyOptions),
    [tenantsForVertical, currencyOptions]
  );

  const tenantSourceOptions = useMemo(() => {
    if (enrichedTenantsForVertical.length > ZERO) {
      return enrichedTenantsForVertical;
    }

    return apiTenantOptions;
  }, [enrichedTenantsForVertical, apiTenantOptions]);

  const tenantOptions = useMemo(
    () =>
      buildCouponFormTenantOptions({
        hasDiscountType,
        hasSelectedVertical,
        tenantIds: values.tenantIds,
        enrichedTenantsForVertical,
        apiTenantOptions,
      }),
    [
      enrichedTenantsForVertical,
      apiTenantOptions,
      values.tenantIds,
      hasSelectedVertical,
      hasDiscountType,
    ]
  );

  const selectedCurrencyIds = useMemo(() => {
    if (!areTenantsConfirmed && isCreate) {
      return [];
    }

    const currencyIdsFromTenants = getSelectedCurrencyIdsFromTenants(
      tenantSourceOptions,
      selectedTenantIds
    );

    if (currencyIdsFromTenants.length > ZERO) {
      return currencyIdsFromTenants;
    }

    const fallbackCurrencyId = String(values.currencyId ?? "").trim();
    if (fallbackCurrencyId !== "") {
      return [fallbackCurrencyId];
    }

    return Object.keys(values.currencySettings ?? {});
  }, [
    areTenantsConfirmed,
    isCreate,
    tenantSourceOptions,
    selectedTenantIds,
    values.currencyId,
    values.currencySettings,
  ]);

  const {
    data: basicPackages = [],
    isLoading: isBasicPackagesLoading,
    isError: isBasicPackagesError,
    error: basicPackagesError,
    refetch: refetchBasicPackages,
  } = useBasicPackagesInfo(selectedTenantIds, shouldFetchPackages);

  const availablePackageIds = useMemo(
    () => basicPackages.map((packageEntry) => packageEntry.packageId),
    [basicPackages]
  );

  const couponFormValidationContext = useMemo(
    () => ({
      availablePackageIds,
      availableTenantOptionIds: getTenantOptionIds(tenantOptions),
      selectedCurrencyIds,
      areTenantsConfirmed,
    }),
    [
      availablePackageIds,
      tenantOptions,
      selectedCurrencyIds,
      areTenantsConfirmed,
    ]
  );

  const requiresLoadedPackageList =
    hasSelectedTenants && values.selectAllPackages === false;

  const requiresLoadedTenantOptions =
    shouldLoadTenants && hasSelectedTenants;

  const isPackageListReadyForValidation =
    !requiresLoadedPackageList ||
    (!isBasicPackagesLoading && !isBasicPackagesError);

  const isTenantOptionsReadyForValidation =
    !requiresLoadedTenantOptions ||
    (!isTenantOptionsLoading && !isTenantOptionsError);

  const runCouponFormValidation = useCallback(() => {
    if (isCreate) {
      return validateCreateCouponForm(values, couponFormValidationContext);
    }

    if (isEdit) {
      return validateEditCouponForm(values, couponFormValidationContext);
    }

    return { errors: {}, isValid: false };
  }, [isCreate, isEdit, values, couponFormValidationContext]);

  const isFormSubmittable = useMemo(() => {
    if (
      isView ||
      !isPackageListReadyForValidation ||
      !isTenantOptionsReadyForValidation
    ) {
      return false;
    }

    return runCouponFormValidation().isValid;
  }, [
    isView,
    isPackageListReadyForValidation,
    isTenantOptionsReadyForValidation,
    runCouponFormValidation,
  ]);

  const computedStatus = resolveCouponStatus(
    values.effectiveDate,
    values.expiryDate,
    new Date(),
    values.status
  );
  const statusLabel = getCouponStatusLabel(computedStatus);

  const validityDays = useMemo(
    () => getCouponValidityDays(values.effectiveDate, values.expiryDate),
    [values.effectiveDate, values.expiryDate]
  );

  const getStepSubtitle = useCallback(
    (stepId) => getCouponFormStepSubtitle(stepId, isView),
    [isView]
  );

  const previewPackageData = useMemo(
    () =>
      buildPreviewPackageData({
        values,
        isCreate,
        isEdit,
        basicPackages,
      }),
    [values, isCreate, isEdit, basicPackages]
  );

  const previewPlatformItems = useMemo(
    () => buildPreviewPlatformItems(values),
    [values]
  );

  const getValidityPreviewItems = useCallback(
    () => buildValidityPreviewItems(values.effectiveDate, values.expiryDate),
    [values.effectiveDate, values.expiryDate]
  );

  const getDiscountPreviewItems = useCallback(
    () =>
      buildDiscountPreviewItems(values, currencyOptions, selectedCurrencyIds),
    [values, currencyOptions, selectedCurrencyIds]
  );

  const getUsagePreviewItems = useCallback(
    () => buildUsagePreviewItems(values, { isEdit, isView }),
    [values, isEdit, isView]
  );

  const toggleSectionCollapse = useCallback((sectionKey) => {
    setCollapsedSections((previousSections) => ({
      ...previousSections,
      [sectionKey]: !previousSections[sectionKey],
    }));
  }, []);

  const isSectionCollapsed = useCallback(
    (sectionKey) => Boolean(collapsedSections[sectionKey]),
    [collapsedSections]
  );

  const scrollToValidationStep = useCallback((stepId) => {
    document.getElementById(`step-${stepId}`)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, []);

  const clearScopeFieldErrors = useCallback((fieldNames) => {
    setErrors((previousErrors) => {
      const nextErrors = { ...previousErrors };
      fieldNames.forEach((fieldName) => {
        delete nextErrors[fieldName];
      });
      return nextErrors;
    });
  }, []);

  const handleVerticalChange = useCallback(
    (nextVerticalId) => {
      setAreTenantsConfirmed(false);
      setValues((previousValues) =>
        applyVerticalScopeChange(previousValues, nextVerticalId)
      );
      clearScopeFieldErrors(VERTICAL_SCOPE_ERROR_FIELDS);
    },
    [clearScopeFieldErrors]
  );

  const handleTenantsChange = useCallback(
    (nextTenantIds) => {
      setAreTenantsConfirmed(false);
      setValues((previousValues) =>
        applyTenantsScopeChange(previousValues, nextTenantIds)
      );
      clearScopeFieldErrors(TENANT_SCOPE_ERROR_FIELDS);
    },
    [clearScopeFieldErrors]
  );

  const handleConfirmTenants = useCallback(() => {
    if (selectedTenantIds.length === ZERO) {
      setErrors((previousErrors) => ({
        ...previousErrors,
        tenantIds: "Select at least one tenant before continuing.",
      }));
      openSnackbar("Select at least one tenant before continuing.", "error");
      return;
    }

    const currencyIds = getSelectedCurrencyIdsFromTenants(
      enrichedTenantsForVertical,
      selectedTenantIds
    );

    if (currencyIds.length === ZERO) {
      setErrors((previousErrors) => ({
        ...previousErrors,
        tenantIds:
          "Selected tenants have no currency configured. Choose different tenants.",
      }));
      openSnackbar(
        "Selected tenants have no currency configured.",
        "error"
      );
      return;
    }

    setValues((previousValues) =>
      applyConfirmedTenantScope(previousValues, {
        tenantsForVertical: enrichedTenantsForVertical,
      })
    );
    setAreTenantsConfirmed(true);
    clearScopeFieldErrors(TENANT_SCOPE_ERROR_FIELDS);

    const currencyLabels = currencyIds
      .map((currencyId) => getCurrencyLabel(currencyOptions, currencyId))
      .filter(Boolean)
      .join(", ");

    openSnackbar(
      currencyIds.length === 1
        ? `Tenants confirmed. Loading packages for ${currencyLabels || "the selected currency"}.`
        : `Tenants confirmed for ${currencyIds.length} currencies: ${currencyLabels}.`,
      "success"
    );
  }, [
    selectedTenantIds,
    enrichedTenantsForVertical,
    clearScopeFieldErrors,
    openSnackbar,
    currencyOptions,
  ]);

  const updateField = useCallback((field, value) => {
    let nextValues;

    setValues((previousValues) => {
      nextValues = buildNextValues(previousValues, field, value);
      return nextValues;
    });

    setErrors((previousErrors) => {
      const nextErrors = { ...previousErrors };
      if (nextErrors[field]) {
        delete nextErrors[field];
      }

      if (field === "discountType") {
        DISCOUNT_TYPE_SCOPE_ERROR_FIELDS.forEach((scopeField) => {
          delete nextErrors[scopeField];
        });
        delete nextErrors.discountValue;
      }

      if (
        field === "discountType" &&
        value === DISCOUNT_TYPES.PERCENTAGE &&
        nextValues &&
        isPercentageDiscountOverMax(
          nextValues.discountValue,
          DISCOUNT_TYPES.PERCENTAGE
        )
      ) {
        nextErrors.discountValue =
          "Percentage discount must be between 0 and 100.";
      }

      return nextErrors;
    });
  }, []);

  const updateCurrencySettingField = useCallback(
    (currencyId, field, value) => {
      if (value !== "" && !AMOUNT_INPUT_PATTERN.test(value)) {
        return;
      }

      setValues((previousValues) =>
        buildNextCurrencySettingsValues(previousValues, currencyId, field, value)
      );

      setErrors((previousErrors) => {
        if (!previousErrors.currencySettings) {
          return previousErrors;
        }

        const nextErrors = { ...previousErrors };
        const currencyErrors = {
          ...(nextErrors.currencySettings ?? {}),
        };
        const fieldErrors = {
          ...(currencyErrors[String(currencyId)] ?? {}),
        };

        delete fieldErrors[field];

        if (Object.keys(fieldErrors).length === ZERO) {
          delete currencyErrors[String(currencyId)];
        } else {
          currencyErrors[String(currencyId)] = fieldErrors;
        }

        if (Object.keys(currencyErrors).length === ZERO) {
          delete nextErrors.currencySettings;
        } else {
          nextErrors.currencySettings = currencyErrors;
        }

        if (field === "minimumCartMrp") {
          delete nextErrors.category;
        }

        return nextErrors;
      });
    },
    []
  );

  const updateIntegerField = useCallback(
    (field, value, { min, max, blockBelowMin = false } = {}) => {
      if (value !== "" && !/^\d+$/.test(value)) return;

      const normalizedValue = normalizeIntegerInput(value);

      if (normalizedValue !== "") {
        const numericValue = Number(normalizedValue);

        if (!Number.isNaN(numericValue)) {
          if (max !== undefined && numericValue > max) return;

          if (min !== undefined && numericValue < min && blockBelowMin) {
            return;
          }
        }
      }

      updateField(field, normalizedValue);
      setErrors((previousErrors) => {
        const nextErrors = { ...previousErrors };
        delete nextErrors[field];

        if (normalizedValue !== "" && min !== undefined) {
          const numericValue = Number(normalizedValue);
          if (!Number.isNaN(numericValue) && numericValue < min) {
            nextErrors[field] = getTotalCouponCountMinError();
          }
        }

        return nextErrors;
      });
    },
    [updateField]
  );

  const updateAmountField = useCallback(
    (field, value) => {
      if (value !== "" && !AMOUNT_INPUT_PATTERN.test(value)) return;

      const normalizedValue = normalizeDecimalInput(value);

      if (
        field === "discountValue" &&
        isPercentageDiscountOverMax(normalizedValue, values.discountType)
      ) {
        return;
      }

      updateField(field, normalizedValue);
      setErrors((previousErrors) => {
        if (!previousErrors[field]) return previousErrors;
        const nextErrors = { ...previousErrors };
        delete nextErrors[field];
        return nextErrors;
      });
    },
    [updateField, values.discountType]
  );

  const handlePlatformsChange = useCallback(({ selectAll, platformIds }) => {
    setValues((previousValues) => ({
      ...previousValues,
      selectAllPlatforms: selectAll,
      platformIds,
    }));

    setErrors((previousErrors) => {
      if (!previousErrors.selectAllPlatforms && !previousErrors.platformIds) {
        return previousErrors;
      }

      const nextErrors = { ...previousErrors };
      delete nextErrors.selectAllPlatforms;
      delete nextErrors.platformIds;
      return nextErrors;
    });
  }, []);

  const handlePackagesChange = useCallback(({ selectAll, packageIds }) => {
    setValues((previousValues) => ({
      ...previousValues,
      selectAllPackages: selectAll,
      packageIds,
    }));

    setErrors((previousErrors) => {
      if (!previousErrors.selectAllPackages && !previousErrors.packageIds) {
        return previousErrors;
      }

      const nextErrors = { ...previousErrors };
      delete nextErrors.selectAllPackages;
      delete nextErrors.packageIds;
      return nextErrors;
    });
  }, []);

  const handleSubmit = useCallback(async () => {
    if (isView || isSubmitting) return;

    if (!isPackageListReadyForValidation) {
      openSnackbar("Packages are still loading. Please wait.", "error");
      return;
    }

    if (!isTenantOptionsReadyForValidation) {
      openSnackbar("Tenants are still loading. Please wait.", "error");
      return;
    }

    const validation = runCouponFormValidation();
    setErrors(validation.errors);

    if (!validation.isValid) {
      openSnackbar("Please fix the highlighted validation errors.", "error");
      const firstErrorField = Object.keys(validation.errors)[ZERO];
      scrollToValidationStep(
        STEP_BY_FIELD[firstErrorField] ?? COUPON_FORM_DEFAULT_STEP_ID
      );
      return;
    }

    if (onSubmit) {
      await onSubmit(values, {
        tenants: enrichedTenantsForVertical,
        currencyOptions,
        selectedCurrencyIds,
        availablePackageIds,
      });
    }
  }, [
    isView,
    isSubmitting,
    isPackageListReadyForValidation,
    isTenantOptionsReadyForValidation,
    runCouponFormValidation,
    values,
    onSubmit,
    openSnackbar,
    scrollToValidationStep,
    enrichedTenantsForVertical,
    currencyOptions,
    selectedCurrencyIds,
    availablePackageIds,
  ]);

  const handleCopyCode = useCallback(async () => {
    if (values.code === "" || values.code === null || values.code === undefined) {
      return;
    }

    try {
      await navigator.clipboard.writeText(values.code);
      setCodeCopied(true);
      openSnackbar("Coupon code copied", "success");
      window.setTimeout(
        () => setCodeCopied(false),
        COUPON_COPY_FEEDBACK_DURATION_MS
      );
    } catch {
      openSnackbar("Failed to copy coupon code", "error");
    }
  }, [values.code, openSnackbar]);

  return {
    mode,
    isView,
    isCreate,
    isEdit,
    submitLabel,
    isFormSubmittable,
    isSubmitting,
    values,
    errors,
    updateField,
    updateIntegerField,
    updateAmountField,
    updateCurrencySettingField,
    handlePlatformsChange,
    handlePackagesChange,
    handleSubmit,
    toggleSectionCollapse,
    isSectionCollapsed,
    previewPackageSearch,
    setPreviewPackageSearch,
    codeCopied,
    handleCopyCode,
    getStepSubtitle,
    computedStatus,
    statusLabel,
    validityDays,
    basicPackages,
    isBasicPackagesLoading,
    isBasicPackagesError,
    basicPackagesError,
    refetchBasicPackages,
    getValidityPreviewItems,
    getDiscountPreviewItems,
    getUsagePreviewItems,
    previewPackageData,
    previewPlatformItems,
    navigate,
    verticalOptions,
    tenantOptions,
    currencyOptions,
    isVerticalOptionsLoading,
    isTenantOptionsLoading,
    isCurrencyOptionsLoading,
    isVerticalOptionsError,
    isTenantOptionsError,
    isCurrencyOptionsError,
    verticalOptionsError,
    tenantOptionsError,
    currencyOptionsError,
    refetchVerticalOptions,
    refetchTenantOptions,
    refetchCurrencyOptions,
    handleVerticalChange,
    handleTenantsChange,
    handleConfirmTenants,
    handleEditTenants: () => setAreTenantsConfirmed(false),
    areTenantsConfirmed,
    hasSelectedTenants,
    hasSelectedVertical,
    hasDiscountType,
    isFlatDiscount: isFlat,
    isPercentageDiscount: isPercentage,
    selectedCurrencyIds,
    selectedTenantIds,
    enrichedTenantsForVertical,
    isTenantDropdownDisabled:
      !hasDiscountType ||
      !hasSelectedVertical ||
      isTenantOptionsLoading ||
      (isCreate && areTenantsConfirmed),
    isVerticalDropdownDisabled: !hasDiscountType || isVerticalOptionsLoading,
  };
};

export default useCouponForm;
